import { BookingV4 } from "../../types/bookings";
import { CollinsUser } from "../../types/user";
import { dmnStorage } from "../../utils/dmnStorage";
import {
  addOrUpdateBooking,
  mapBookingV4ToState,
  mapBookingToV4
} from "./UpdateBooking";
import { errorHandler, apiFetchNotOkHandler } from "../errorHandler";
import { PaymentFormData } from "../../components/Pages/Diary/SelectedBooking/Payments/AddPaymentModal";
import { DiaryContextType } from "../../context/DiaryContext";
import { dmnIdentity } from "../../utils/dmnIdentity";
import { getEnvironmentProps } from "../../utils/environment";

type AddDepositOptions = {
  paymentFor?: string;
};

export const addDeposit = async (
  booking: BookingV4,
  paymentData: PaymentFormData,
  diaryContext?: DiaryContextType,
  options?: AddDepositOptions
): Promise<any> => {
  const currentUser: CollinsUser | null = await dmnStorage.get("user");

  if (!currentUser?._id || !currentUser?.first_name) {
    throw new Error("User information not available");
  }

  const newDeposit: any = {
    date: paymentData.date
      ? paymentData.date.toISOString()
      : new Date().toISOString(),
    amount: paymentData.amount,
    type: paymentData.type as string,
    paid_type: paymentData.paidType ?? "",
    payment_for: options?.paymentFor ?? "",
    notes: paymentData.notes,
    customer_note: paymentData.customerNote ?? "",
    currency: booking.deposits?.[0]?.currency || "GBP",
    email_receipt: paymentData.emailReceipt || false,
    user: {
      id: currentUser._id,
      name: `${currentUser.first_name} ${currentUser.last_name ?? ""}`.trim()
    }
  };

  if (newDeposit.type === "paid") {
    newDeposit.status = "paid";
  }

  if (paymentData.type === "paid" && paymentData.emailReceipt) {
    newDeposit.push_to_epos = false;
  }

  const updatedBooking: BookingV4 = {
    ...booking,
    deposits: [...(booking.deposits || []), newDeposit]
  };

  const viewBooking = mapBookingV4ToState(updatedBooking);

  const result = await addOrUpdateBooking({
    booking: viewBooking,
    diaryContext
  });

  if (!result) {
    throw new Error("Failed to update booking with new deposit");
  }

  const bookingV4 = mapBookingToV4(result);

  return { booking: bookingV4 };
};

export const claimDeposit = async (
  booking: BookingV4,
  depositId: string,
  amount?: number
): Promise<any> => {
  const token = await dmnIdentity.getAccessToken();
  const environmentVars = getEnvironmentProps(token);

  const claimRequest = {
    amount: amount ? amount * 100 : 0,
    notes: ""
  };

  let response;

  try {
    response = await fetch(
      `${environmentVars?.COLLINS_API_BASE_URL}/bookings/${booking._id}/deposits/${depositId}/claim-auth`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(claimRequest)
      }
    );
  } catch (error) {
    errorHandler(error, "Error claiming deposit");
    return {};
  }

  if (!response?.ok) {
    apiFetchNotOkHandler(response, "Error claiming deposit");
    return {};
  }

  const result = await response.json();

  if (result?.claim) {
    const claimData = {
      created_date: new Date(result.claim.created_date),
      amount: amount,
      payment_ref: result.claim.payment_ref
    };

    const updatedDeposits = booking.deposits?.map((dep) => {
      if (dep.id === depositId) {
        return {
          ...dep,
          claims: [...(dep.claims || []), claimData]
        };
      }
      return dep;
    });

    return {
      booking: {
        ...booking,
        deposits: updatedDeposits
      }
    };
  }

  return result;
};

export const refundDeposit = async (
  booking: BookingV4,
  depositId: string,
  amount: number,
  notes?: string
): Promise<any> => {
  const token = await dmnIdentity.getAccessToken();
  const environmentVars = getEnvironmentProps(token);

  const refundRequest = {
    amount: amount * 100,
    notes: notes ?? ""
  };

  let response;

  try {
    response = await fetch(
      `${environmentVars?.COLLINS_API_BASE_URL}/bookings/${booking._id}/deposits/${depositId}/refund`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(refundRequest)
      }
    );
  } catch (error) {
    errorHandler(error, "Error processing refund");
    return {};
  }

  if (!response?.ok) {
    apiFetchNotOkHandler(response, "Error processing refund");
    return {};
  }

  const result = await response.json();

  if (result?.refund || result?.successful) {
    const refundData = {
      created_date: new Date(),
      amount: amount,
      notes: notes ?? "",
      refund_ref: result.refund?.refund_ref || result.refund?._id || "",
      id: result.refund?._id || ""
    };

    const updatedDeposits = booking.deposits?.map((dep) => {
      if (dep.id === depositId) {
        return {
          ...dep,
          refund_ids: [...(dep.refund_ids || []), refundData.id],
          refunds: [...(dep.refunds || []), refundData.id]
        };
      }
      return dep;
    });

    return {
      booking: {
        ...booking,
        deposits: updatedDeposits,
        refunds: [...(booking.refunds || []), refundData]
      }
    };
  }

  return result;
};

export const deleteDeposit = async (
  booking: BookingV4,
  depositId: string,
  diaryContext?: DiaryContextType
): Promise<BookingV4 | undefined> => {
  const updatedBooking: BookingV4 = {
    ...booking,
    deposits: booking.deposits?.filter((deposit) => deposit.id !== depositId)
  };

  const viewBooking = mapBookingV4ToState(updatedBooking);

  const result = await addOrUpdateBooking({
    booking: viewBooking,
    diaryContext
  });

  if (!result) {
    return undefined;
  }

  return mapBookingToV4(result);
};

export const getBookingSignature = async (
  bookingId: string
): Promise<string> => {
  try {
    const token = await dmnIdentity.getAccessToken();
    const environmentVars = getEnvironmentProps(token);

    const response = await fetch(
      `${environmentVars?.COLLINS_API_BASE_URL}/signature`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ bookingId })
      }
    );

    if (!response?.ok) {
      console.error("Failed to get booking signature");
      return "";
    }

    const data = await response.json();
    return data?.signature || "";
  } catch (error) {
    console.error("Failed to get booking signature:", error);
    return "";
  }
};
